import React from 'react';
import { Row, Col } from 'syngenta-digital-cropwise-react-ui-kit';

/**
 * # FormGrid - Reusable Form Layout Component
 *
 * A flexible grid system for building consistent form layouts. Takes configuration objects
 * instead of hardcoded JSX, making it easy to reuse across different forms.
 *
 * ## Smart Label Spacing
 * - If any field in a row has a label, label spacing is applied to all fields in that row
 * - If no fields in a row have labels, label spacing is automatically removed for that row
 * - This prevents unnecessary whitespace when rows contain only buttons or unlabeled components
 *
 * ## Basic Usage
 * ```typescript
 * import FormGrid, { FormRowConfig } from 'components/FormGrid';
 * import { COL_SPAN_8, COL_SPAN_12 } from 'components/FormGrid/constants';
 *
 * const formRows: FormRowConfig[] = [
 *   {
 *     id: 'row-1',
 *     fields: [
 *       {
 *         id: 'name',
 *         label: 'Name', // Optional - spacing maintained if omitted
 *         colSpan: COL_SPAN_12,
 *         required: true,
 *         error: errors.name,
 *         render: () => <Input name="name" value={values.name} onChange={handleChange} />
 *       },
 *       {
 *         id: 'actions',
 *         // No label - spacing still maintained for alignment
 *         colSpan: COL_SPAN_12,
 *         render: () => <Button>Action</Button>
 *       }
 *     ]
 *   }
 * ];
 *
 * return <FormGrid rows={formRows} />;
 * ```
 *
 * ## Hide Fields by Context
 * Use `excludeFields` to conditionally hide fields:
 * ```typescript
 * // Hide specific fields in edit mode
 * <FormGrid rows={formRows} excludeFields={['applicationId']} />
 * ```
 */

export interface FormFieldConfig {
  id: string;
  label?: string | React.ReactNode;
  colSpan: number;
  colStyle?: React.CSSProperties;
  required?: boolean;
  divider?: boolean;
  render: () => React.ReactNode;
  error?: string;
  isHeader?: boolean;
}

export interface FormRowConfig {
  id: string;
  fields: FormFieldConfig[];
  rowClassName?: string;
  showErrorLineConditionally?: boolean;
}

interface FormGridProps {
  rows: FormRowConfig[];
  gutter?: [number, number];
  className?: string;
  excludeFields?: string[];
  showErrorLineConditionally?: boolean; // for few optional fields the error line not needed
}

const DEFAULT_GUTTER: [number, number] = [24, 24];
const MIN_HEIGHT = 20;

const FormGrid: React.FC<FormGridProps> = ({
  rows,
  gutter = DEFAULT_GUTTER,
  className = 'flex flex-col gap-1',
  excludeFields = [],
  showErrorLineConditionally = false
}) => {
  const renderError = (error?: string) => (
    <div
      style={{ minHeight: MIN_HEIGHT }}
      className="text-red-500 text-xs leading-5"
    >
      {error ?? ''}
    </div>
  );

  const renderField = (
    field: FormFieldConfig,
    hasAnyLabelsInRow: boolean,
    rowShowErrorLineConditionally: boolean
  ) => (
    <Col
      key={field.id}
      span={field.colSpan}
      prefixCls="syt-antd-col"
      className={field.divider ? 'border-r border-neutral-200' : undefined}
      style={field.colStyle}
    >
      <div className="flex flex-col">
        {hasAnyLabelsInRow && (
          <label className="text-neutral-60 mb-2px text-sm leading-4">
            {field.label && (
              <>
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </>
            )}
            {!field.label && <span>&nbsp;</span>}
          </label>
        )}
        {field.render()}
        {field.isHeader || (rowShowErrorLineConditionally && !field.error)
          ? null
          : renderError(field.error)}
      </div>
    </Col>
  );

  const renderRow = (row: FormRowConfig) => {
    const filteredFields = row.fields.filter(
      field => !excludeFields.includes(field.id)
    );

    // Skip rendering the row if all fields are excluded
    if (filteredFields.length === 0) {
      return null;
    }

    // Check if any field in this row has a label
    const hasAnyLabelsInRow = filteredFields.some(field => !!field.label);

    // Row-level override wins over the grid-level default.
    const rowShowErrorLineConditionally =
      row.showErrorLineConditionally ?? showErrorLineConditionally;

    return (
      <Row
        key={row.id}
        gutter={gutter}
        prefixCls="syt-antd-row"
        className={row.rowClassName}
      >
        {filteredFields.map(field =>
          renderField(field, hasAnyLabelsInRow, rowShowErrorLineConditionally)
        )}
      </Row>
    );
  };

  return <div className={className}>{rows.map(renderRow)}</div>;
};

export default FormGrid;
